import { useEffect, useState, type CSSProperties } from "react";
import { C, clamp, hexA } from "../theme";
import type { Zone } from "../types";

/** Per-state liquid gradient (top→bottom). */
function liquidGradient(z: Zone): string {
  if (z.state === "watering") return `linear-gradient(180deg, ${C.sea}, ${C.seaDeep})`;
  if (z.moisture <= z.mad) return `linear-gradient(180deg, ${C.beacon}, ${C.beaconDeep})`;
  return `linear-gradient(180deg, ${C.sage}, ${C.sageDeep})`;
}

/**
 * A thin repeating sine wave drawn as an SVG data URI, tiled horizontally.
 * Tile is 64px wide so it lines up with the 0→64px translate animation.
 */
const WAVE_SVG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='8' viewBox='0 0 64 8'%3E%3Cpath d='M0 5 C 8 1, 24 1, 32 5 S 56 9, 64 5 L64 8 L0 8 Z' fill='%23ffffff'/%3E%3C/svg%3E\")";

/** The signature liquid gauge: an animated water-fill vessel with target band + refill line. */
export function Reservoir({ z, w = 64, h = 138 }: { z: Zone; w?: number; h?: number }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const moisture = clamp(z.moisture, 0, 100);
  const [bandLo, bandHi] = z.band;
  const waveH = Math.max(6, Math.round(w * 0.12)); // wave crest band height, scales with size

  const belowRefill = moisture <= z.mad;
  const gaugeLabel =
    z.state === "watering"
      ? `${z.name}: soil moisture ${Math.round(moisture)} percent, watering now.`
      : `${z.name}: soil moisture ${Math.round(moisture)} percent, ${
          belowRefill ? "below" : "above"
        } the ${z.mad} percent refill line.`;

  const vessel: CSSProperties = {
    position: "relative",
    width: w,
    height: h,
    flexShrink: 0,
    borderRadius: w * 0.23,
    background: `linear-gradient(180deg, ${C.paper} 0%, ${C.sand} 100%)`,
    border: `1px solid ${C.hairline}`,
    boxShadow: "var(--shadow-gauge)",
    overflow: "hidden",
  };

  const targetBand: CSSProperties = {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: `${bandLo}%`,
    height: `${Math.max(0, bandHi - bandLo)}%`,
    background: hexA(C.sage, 0.13),
  };

  const fill: CSSProperties = {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: mounted ? `${moisture}%` : 0,
    background: liquidGradient(z),
    transition: "height 1.5s cubic-bezier(.2,.85,.25,1)",
  };

  const crest: CSSProperties = {
    position: "absolute",
    left: 0,
    right: 0,
    top: -waveH,
    height: waveH,
    backgroundImage: WAVE_SVG,
    backgroundRepeat: "repeat-x",
    backgroundSize: "64px 100%",
    opacity: 0.55,
    animation: "wave 5.5s linear infinite",
  };

  const refillLine: CSSProperties = {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: `${clamp(z.mad, 0, 100)}%`,
    borderTop: `1px dashed ${hexA(C.beacon, 0.85)}`,
  };

  const sheen: CSSProperties = {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    width: Math.round(w * 0.32),
    background: `linear-gradient(90deg, ${C.paper} 0%, ${hexA(C.paper, 0)} 100%)`,
    opacity: 0.55,
    pointerEvents: "none",
  };

  return (
    <div style={vessel} role="img" aria-label={gaugeLabel}>
      <div style={targetBand} aria-hidden />
      <div style={fill} aria-hidden>
        <div style={crest} />
      </div>
      <div style={refillLine} aria-hidden />
      <div style={sheen} aria-hidden />
    </div>
  );
}
